import { InjectableService } from '@/shared/services/InjectableService'
import type { AxiosResponse } from 'axios'
import { submitApiRequest, getUserPath, HttpMethod } from '@/services/aplosApi'
import type { Folder } from '../models/Folder'
import type {
  CreateFolderRequest,
  UpdateFolderRequest,
  BulkMoveResult,
  FolderListResponse,
} from '../models/FolderRequests'

export interface IFolderApiService {
  listFolders(tenantId: string, userId: string): Promise<AxiosResponse<FolderListResponse>>
  createFolder(tenantId: string, userId: string, data: CreateFolderRequest): Promise<AxiosResponse<Folder>>
  updateFolder(tenantId: string, userId: string, folderId: string, data: UpdateFolderRequest): Promise<AxiosResponse<Folder>>
  deleteFolder(tenantId: string, userId: string, folderId: string): Promise<AxiosResponse<void>>
  moveToFolder(tenantId: string, userId: string, executionId: string, folderId: string | null): Promise<AxiosResponse<void>>
  bulkMove(tenantId: string, userId: string, executionIds: string[], folderId: string | null): Promise<AxiosResponse<BulkMoveResult>>
}

export class FolderApiService extends InjectableService implements IFolderApiService {

  static readonly injectableKey = 'IFolderApiService'

  get injectableKey() {
    return (this.constructor as typeof FolderApiService).injectableKey
  }

  async listFolders(tenantId: string, userId: string): Promise<AxiosResponse<FolderListResponse>> {
    const basePath = getUserPath(tenantId, userId)
    return await submitApiRequest(HttpMethod.GET, `${basePath}/execution-folders`)
  }

  async createFolder(tenantId: string, userId: string, data: CreateFolderRequest): Promise<AxiosResponse<Folder>> {
    const basePath = getUserPath(tenantId, userId)
    return await submitApiRequest(HttpMethod.POST, `${basePath}/execution-folders`, data)
  }

  async updateFolder(tenantId: string, userId: string, folderId: string, data: UpdateFolderRequest): Promise<AxiosResponse<Folder>> {
    const basePath = getUserPath(tenantId, userId)
    return await submitApiRequest(HttpMethod.PUT, `${basePath}/execution-folders/${folderId}`, data)
  }

  async deleteFolder(tenantId: string, userId: string, folderId: string): Promise<AxiosResponse<void>> {
    const basePath = getUserPath(tenantId, userId)
    return await submitApiRequest(HttpMethod.DELETE, `${basePath}/execution-folders/${folderId}`)
  }

  async moveToFolder(tenantId: string, userId: string, executionId: string, folderId: string | null): Promise<AxiosResponse<void>> {
    const basePath = getUserPath(tenantId, userId)
    return await submitApiRequest(HttpMethod.PUT, `${basePath}/execution-summaries/${executionId}/folder`, { folderId })
  }

  async bulkMove(tenantId: string, userId: string, executionIds: string[], folderId: string | null): Promise<AxiosResponse<BulkMoveResult>> {
    const basePath = getUserPath(tenantId, userId)
    return await submitApiRequest(HttpMethod.POST, `${basePath}/execution-summaries/bulk-move`, { executionIds, folderId })
  }
}
